import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Divider, TextField, Typography } from "@mui/material";
import { EditOutlined, NoteAddOutlined } from "@mui/icons-material";
import { Order } from "../../../types";
import { orderService } from "../../../services/orderService";
import { Notify, useQuickEditSave } from "./useQuickEditSave";

interface OrderNoteEditorProps {
    order: Order;
    /** Whether quick edits are allowed (staff login, order not cancelled). */
    editable: boolean;
    onOrderChanged: (order: Order) => void;
    notify: Notify;
    /** Reports unsaved text, so the drawer can ask before discarding it. */
    onDirtyChange: (dirty: boolean) => void;
}

/**
 * The order note in the drawer: shown as before, with Add note / Edit note
 * for quick inline editing. Saves just the note through the same partial
 * order update the courier/channel pickers use. Mount with
 * `key={order.order_id}` so switching orders starts fresh.
 */
export const OrderNoteEditor: React.FC<OrderNoteEditorProps> = ({
    order,
    editable,
    onOrderChanged,
    notify,
    onDirtyChange,
}) => {
    const current = order.notes ?? "";
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(current);
    const inputRef = useRef<HTMLTextAreaElement | null>(null);
    const { saving, error, setError, save } = useQuickEditSave(order.order_id, onOrderChanged, notify);

    const dirty = editing && draft.trim() !== current.trim();
    useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
    useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

    // Focus with the cursor at the end of the existing text.
    useEffect(() => {
        if (!editing) return;
        const el = inputRef.current;
        if (el) {
            el.focus();
            el.setSelectionRange(el.value.length, el.value.length);
        }
    }, [editing]);

    const start = () => {
        setDraft(current);
        setError("");
        setEditing(true);
    };

    const cancel = () => {
        setEditing(false);
        setDraft(current);
        setError("");
    };

    const submit = async () => {
        if (!dirty || saving) return;
        const notes = draft.trim();
        const saved = await save(
            () => orderService.updateOrder(order.order_id, { notes }),
            notes ? "Note saved" : "Note removed",
        );
        if (saved) setEditing(false);
    };

    if (!current && !editing) {
        if (!editable) return null;
        return (
            <>
                <Divider sx={{ my: 2 }} />
                <Button size="small" startIcon={<NoteAddOutlined fontSize="small" />} onClick={start}>
                    Add note
                </Button>
            </>
        );
    }

    return (
        <>
            <Divider sx={{ my: 2 }} />
            <Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                        ORDER NOTES
                    </Typography>
                    {editable && !editing && (
                        <Button
                            size="small"
                            startIcon={<EditOutlined fontSize="small" />}
                            onClick={start}
                            sx={{ my: -0.5 }}
                        >
                            Edit note
                        </Button>
                    )}
                </Box>

                {editing ? (
                    <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                        <TextField
                            inputRef={inputRef}
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                                    e.preventDefault();
                                    void submit();
                                } else if (e.key === "Escape") {
                                    // Cancel the edit without closing the drawer.
                                    e.stopPropagation();
                                    cancel();
                                }
                            }}
                            multiline
                            minRows={3}
                            maxRows={10}
                            fullWidth
                            size="small"
                            placeholder="Delivery instructions, customer requests…"
                            helperText="Also included when the order is sent to Telegram or copied as text."
                            disabled={saving}
                            inputProps={{ "aria-label": "Order note" }}
                        />
                        {error && (
                            <Alert severity="error" sx={{ py: 0 }}>
                                {error}
                            </Alert>
                        )}
                        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1 }}>
                            {/* Keyboard hint only where there's a keyboard. */}
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mr: "auto", display: { xs: "none", sm: "block" } }}
                            >
                                Ctrl/⌘+Enter to save · Esc to cancel
                            </Typography>
                            <Button size="small" color="inherit" onClick={cancel} disabled={saving}>
                                Cancel
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={() => void submit()}
                                disabled={!dirty || saving}
                                sx={{ whiteSpace: "nowrap" }}
                            >
                                {saving ? "Saving…" : "Save note"}
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {current}
                    </Typography>
                )}
            </Box>
        </>
    );
};
